import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { api, imgUrl } from "../../lib/api";
import { PROJECT_CATEGORIES } from "../../data/site";
import { ImageField } from "./ImageField";

const EMPTY = { title: "", cat: "Ristrutturazioni", loc: "", img: "", desc: "", detailsText: "", demo: true };

export const ProjectsTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api("/projects").then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const openEdit = (p) =>
    setEditing(p ? { ...p, detailsText: (p.details || []).join("\n") } : { ...EMPTY });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: editing.title, cat: editing.cat, loc: editing.loc, img: editing.img,
        desc: editing.desc, demo: !!editing.demo,
        details: editing.detailsText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editing.id) await api(`/admin/projects/${editing.id}`, { method: "PUT", body });
      else await api("/admin/projects", { method: "POST", body });
      toast.success("Lavoro salvato.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo lavoro dalla galleria?")) return;
    try {
      await api(`/admin/projects/${id}`, { method: "DELETE" });
      toast.success("Lavoro eliminato.");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!items) return <Loader2 className="animate-spin text-forest-glow mx-auto" size={28} />;

  return (
    <div data-testid="admin-projects">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-cement">{items.length} lavori in galleria</p>
        <button data-testid="admin-project-new" onClick={() => openEdit(null)} className="btn-primary !py-2.5 !px-5 !text-[11px]">
          <Plus size={14} /> Nuovo lavoro
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-project-${p.id}`} className="flex items-center gap-4 bg-card border border-steel p-4">
            <img src={imgUrl(p.img)} alt="" className="w-16 h-16 object-cover shrink-0 border border-steel" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{p.title}</p>
              <p className="text-cement text-xs mt-0.5">{p.cat} · {p.loc} {p.demo && <span className="text-forest-glow ml-1">· demo</span>}</p>
            </div>
            <button data-testid={`admin-project-edit-${p.id}`} onClick={() => openEdit(p)} aria-label={`Modifica ${p.title}`} className="w-9 h-9 flex items-center justify-center border border-steel text-metal hover:border-forest-glow hover:text-forest-glow transition-colors"><Pencil size={14} /></button>
            <button data-testid={`admin-project-delete-${p.id}`} onClick={() => del(p.id)} aria-label={`Elimina ${p.title}`} className="w-9 h-9 flex items-center justify-center border border-steel text-metal hover:border-red-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="admin-project-modal">
          <div className="absolute inset-0 bg-base/90" onClick={() => setEditing(null)} aria-hidden="true" />
          <div className="relative bg-card border border-steel w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-lg text-white uppercase">{editing.id ? "Modifica lavoro" : "Nuovo lavoro"}</h3>
              <button data-testid="admin-project-close" onClick={() => setEditing(null)} aria-label="Chiudi" className="w-9 h-9 flex items-center justify-center border border-steel text-metal hover:border-forest-glow"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="admin-project-title" className="field-input" placeholder="Titolo *" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-4">
                <select data-testid="admin-project-cat" className="field-input cursor-pointer" value={editing.cat} onChange={(e) => setEditing({ ...editing, cat: e.target.value })}>
                  {PROJECT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input data-testid="admin-project-loc" className="field-input" placeholder="Località (es. Mestre VE)" value={editing.loc} onChange={(e) => setEditing({ ...editing, loc: e.target.value })} />
              </div>
              <textarea data-testid="admin-project-desc" className="field-input" rows={2} placeholder="Descrizione" value={editing.desc} onChange={(e) => setEditing({ ...editing, desc: e.target.value })} />
              <textarea data-testid="admin-project-details" className="field-input" rows={3} placeholder={"Dettagli tecnici, uno per riga"} value={editing.detailsText} onChange={(e) => setEditing({ ...editing, detailsText: e.target.value })} />
              <ImageField value={editing.img} onChange={(url) => setEditing({ ...editing, img: url })} testid="admin-project-image" />
              <label className="flex items-center gap-3 text-sm text-cement cursor-pointer">
                <input data-testid="admin-project-demo" type="checkbox" checked={!!editing.demo} onChange={(e) => setEditing({ ...editing, demo: e.target.checked })} className="w-4 h-4 accent-[#2A594B]" />
                Mostra badge "Demo" (contenuto dimostrativo)
              </label>
              <button data-testid="admin-project-save" onClick={save} disabled={saving || !editing.title} className="btn-primary w-full disabled:opacity-60">
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Salva lavoro"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
